<template><div class="unit-vpn-update">
  <modal-dialog v-if="uploading" @close="cancel" :strong="true">
    {{ filename }}:
    <template v-if="!uploadProgress">
      Starting upload
    </template>
    <template v-else-if="uploadProgress.loaded < uploadProgress.total">
      Uploading {{ Math.round(uploadProgress.loaded / 1024) }} KiB
      / {{ Math.round(uploadProgress.total / 1024) }} KiB
      ({{ Math.round(uploadProgress.loaded * 100 / uploadProgress.total) }}%)
    </template>
    <template v-else>
      Uploaded {{ Math.round(uploadProgress.loaded / 1024) }} KiB. <b>Writing software to upload buffer.</b>
    </template>
    <loading />
    <br/>
    <button class="cancelButton" @click="cancel(true)">Cancel</button>
  </modal-dialog>
  <modal-dialog v-else-if="uploadError" @close="uploadError=null" :strong="true">
    {{ filename }}: <span class="err">Error: {{ uploadError }}</span>
  </modal-dialog>
  <modal-dialog v-else-if="commitStatus" :strong="true">
    {{ filename }}: <b>Finalizing upload</b> <loading />
  </modal-dialog>
  <modal-dialog v-else-if="commitOK" @close="$emit('disconnect')" :strong="true">
    <div v-html="commitOK" />
  </modal-dialog>
  <div v-if="info.flash && info.flash.status === 'OK'">
    This system supports remote software updates.
    <table class="software">
      <tr>
        <th />
        <th>Running</th>
        <th>Upload</th>
        <th v-if="anyWarning">Messages</th>
      </tr>
      <tr :class="{warning:kitWarning,confirmed:kitConfirmed}">
        <th>Kit</th>
        <td>K{{info.bld.KIT_NO}} {{info.bld.KIT_NAME}}</td>
        <td v-if="validBldId">K{{bldInfo.KIT_NO}} {{bldInfo.KIT_NAME}}</td>
        <td v-else />
        <td class="msg" v-if="kitWarning" @click="kitConfirmed=!kitConfirmed">
          <span class="material-icons" v-html="kitConfirmed?'check_box':'check_box_outline_blank'" />
          Kit numbers do not match. OK?
        </td>
        <td v-else-if="anyWarning" />
      </tr>
      <tr :class="{warning:versionWarning,confirmed:versionConfirmed}">
        <th>Version</th>
        <td>Rev{{info.bld.MAJOR_REV}}.{{info.bld.MINOR_REV}}</td>
        <td v-if="validBldId">Rev{{bldInfo.MAJOR_REV}}.{{bldInfo.MINOR_REV}}</td>
        <td v-else />
        <td class="msg" v-if="versionWarning" @click="versionConfirmed=!versionConfirmed">
          <span class="material-icons" v-html="versionConfirmed?'check_box':'check_box_outline_blank'" />
          The uploaded build version number is less than the running software. OK?
        </td>
        <td v-else-if="anyWarning" />
      </tr>
      <tr>
        <th>Size</th>
        <td>{{Math.round(info.bld.FLASH_SIZE/1024)}} KB ({{info.bld.FLASH_SIZE}})</td>
        <td v-if="validBldId">{{Math.round(bldInfo.FLASH_SIZE/1024)}} KB ({{bldInfo.FLASH_SIZE}})</td>
        <td v-else />
        <td v-if="anyWarning" />
      </tr>
      <tr>
        <th>CRC</th>
        <td>0x{{info.bld.FLASH_CRC.toString(16).padStart(4, '0')}}</td>
        <td v-if="validBldId">0x{{bldInfo.FLASH_CRC && bldInfo.FLASH_CRC.toString(16).padStart(4, '0')}}</td>
        <td v-else />
        <td v-if="anyWarning" />
      </tr>
      <tr v-if="uploadOK" :class="{warning:awaitingConfirmation}">
        <td/>
        <td/>
        <td/>
        <td v-if="awaitingConfirmation">
          You must confirm the above warning before finalizing this update.
          <b><i>Please note</i></b> that if you load software which does not support remote updates, subsequent software updates will require <b><i>an in-person visit</i></b>.
        </td>
        <td v-else class="err">
          The new software is ready to apply.<br/>
          Press the button below to complete the process.<br/>
          <button v-if="!commitStatus" @click="commit">Finalize Update</button>
          <span v-else v-html="commitStatus" />
          <span v-if="commitError" v-html="commitError"/>
        </td>
      </tr>
    </table>
    <div v-if="filename">
      <b>{{ filename }}</b> selected.
    </div>
    <input type="file" @change="filechosen" ref="file" v-show="false" accept=".bld" />
    <button @click="$refs.file.click()" v-html="filename ? 'Select Different File...' : 'Select File...'" />
  </div>
  <div v-else>
    This system does not support remote software updates.
  </div>
</div></template>
<script>
import Loading from '../Loading'
import ModalDialog from '../ModalDialog'
export default {
  name: 'unit-vpn-update',
  components: { Loading, ModalDialog },
  props: ['api','info'],
  data() {
    return {
      filename: null,
      uploading: false,
      uploadError: null,
      bldInfo: null,
      uploadProgress: null,
      kitConfirmed: false,
      versionConfirmed: false,
      uploadOK: false,
      commitStatus: null,
      commitError: null,
      commitOK: null,
    }
  },
  computed: {
    kitWarning() { return this.validBldId && this.bldInfo.KIT_NO !== this.info.bld.KIT_NO },
    versionWarning() {
      return (
        !this.kitWarning
        && this.validBldId
        && (this.bldInfo.MAJOR_REV < this.info.bld.MAJOR_REV
          || (this.bldInfo.MAJOR_REV === this.info.bld.MAJOR_REV
            && this.bldInfo.MINOR_REV < this.info.bld.MINOR_REV))
      )
    },
    anyWarning() { return this.kitWarning || this.versionWarning },
    validBldId() { return this.bldInfo && ('KIT_NO' in this.bldInfo) },
    awaitingConfirmation() {
      return (
        (this.kitWarning && !this.kitConfirmed)
        || (this.versionWarning && !this.versionConfirmed)
      )
    },
  },
  methods: {
    async filechosen() {
      try {
        this.filename = this.$refs.file.files[0].name
        this.uploading = true
        this.uploadError = null
        this.uploadProgress = null
        this.bldInfo = null
        this.kitConfirmed = false
        this.versionConfirmed = false
        this.uploadOK = false
        this.commitStatus = null
        this.commitError = null
        let o = await this.api.uploadBuild({
          file: this.$refs.file.files[0],
          onUploadProgress: e => { this.uploadProgress = e },
        })
        if (!o) throw new Error()
        this.bldInfo = o.bld
        if (!o.succeeded)
          this.uploadError = o.err || 'Upload did not succeed (no error specified).'
        else
          this.uploadOK = true
      } catch(e) {
        if (e.constructor.name === 'CancelledError') {
          this.uploadError = 'canceled by the user.'
        } else {
          console.error(e)
          this.uploadError = e.message || e.constructor.name
        }
      } finally {
        this.uploading = false
        this.$refs.file.value = this.$refs.file.defaultValue
      }
    },
    async commit() {
      try {
        this.commitStatus = 'Finalizing software...'
        let o = await this.api.finalizeUpload({ bldInfo: this.bldInfo })
        if (!o) throw new Error()
        if (!o.succeeded) {
          this.commitError = o.err || 'Finalization did not succeed (no error specified).'
        } else {
          this.commitOK = '<p>Software successfully finalized.</p><p>Please wait <b>30 seconds</b> before attempting to reconnect.</p>'
        }
      } catch(e) {
        console.error(e)
        this.commitError = e.message || e
      } finally {
        this.commitStatus = null
      }
    },
    cancel(x) {
      if (x) {
        this.api.cancel()
      }
    },
  },
}
</script>
<style lang="scss">
.unit-vpn-update {
  .software {
    border-collapse: collapse;
    margin: 10px 0;
    th, td {
      padding: 5px 10px;
      border: 1px solid #000;
      text-align: left;
      vertical-align: middle;
    }
    td {
      vertical-align: middle;
      &:nth-child(2), &:nth-child(3) { white-space: nowrap; }
    }
    .warning {
      background: #faa;
      .material-icons { vertical-align: top; font-size: 18px; }
      .msg { cursor: pointer; }
      &.confirmed {
        background: #afa;
      }
    }
  }
  .cancelButton {
    float: right;
    margin: 10px 0 0 0;
  }
  .modal-dialog {
    z-index: 500;
    > * {
      width: 80%;
      height: 200px;
    }
  }
}
</style>
